import { GiftOutlined, ShoppingOutlined, WalletOutlined } from '@ant-design/icons';
import { ISmsCreditBalance } from '@interfaces/sms-credits';
import { formatSmsCreditResetDate } from '@utils/sms-credit-reset-date';
import { Alert, Button, Card, Skeleton } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SmsCreditsSectionTitle } from './sms-credits-section-title';

type Props = {
    balance?: ISmsCreditBalance;
    isLoading: boolean;
    isUnavailable: boolean;
    onRetry: () => void;
};

type Tile = {
    key: string;
    icon: ReactNode;
    label: string;
    value: number;
    hint?: string;
    primary?: boolean;
};

/** KPI tiles: Available leads (what a send can use); Included + Purchased say where it comes from and how long it lasts. */
export const SmsCreditBalanceSummary: React.FC<Props> = ({
    balance,
    isLoading,
    isUnavailable,
    onRetry,
}) => {
    const { t, i18n } = useTranslation(['marketplace']);

    // The reset is 00:00 New York time on the 1st: always shown in ET, never in the browser zone.
    const resetDate = formatSmsCreditResetDate(balance?.includedResetsAt, i18n.language);
    const tiles: Tile[] = [
        {
            key: 'available',
            icon: <WalletOutlined />,
            label: t('smsCredits.balance.available'),
            value: balance?.available ?? 0,
            hint: t('smsCredits.balance.availableHint'),
            primary: true,
        },
        {
            key: 'included',
            icon: <GiftOutlined />,
            label: t('smsCredits.balance.included'),
            value: balance?.included ?? 0,
            hint: resetDate ? t('smsCredits.balance.resetsOn', { date: resetDate }) : undefined,
        },
        {
            key: 'purchased',
            icon: <ShoppingOutlined />,
            label: t('smsCredits.balance.purchased'),
            value: balance?.purchased ?? 0,
            hint: t('smsCredits.balance.rollsOver'),
        },
    ];

    return (
        <Card
            title={
                <SmsCreditsSectionTitle icon={<WalletOutlined />}>
                    {t('smsCredits.balance.title')}
                </SmsCreditsSectionTitle>
            }
        >
            {isUnavailable ? (
                <Alert
                    type="warning"
                    showIcon
                    message={t('smsCredits.balance.unavailable')}
                    action={
                        <Button size="small" onClick={onRetry}>
                            {t('smsCredits.balance.retry')}
                        </Button>
                    }
                />
            ) : (
                <div className="sms-credit-kpis" aria-busy={isLoading}>
                    {tiles.map((tile) => (
                        <div
                            key={tile.key}
                            className={`sms-credit-kpi${tile.primary ? ' is-primary' : ''}`}
                        >
                            <div className="sms-credit-kpi-label">
                                <span aria-hidden="true">{tile.icon}</span>
                                {tile.label}
                            </div>
                            {isLoading ? (
                                <Skeleton.Input active size="small" style={{ marginTop: 8 }} />
                            ) : (
                                <div className="sms-credit-kpi-value">
                                    {tile.value.toLocaleString(i18n.language)}
                                    <span className="sms-credit-kpi-unit">
                                        {t('smsCredits.balance.creditsUnit')}
                                    </span>
                                </div>
                            )}
                            {tile.hint && <div className="sms-credit-kpi-hint">{tile.hint}</div>}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};
